import { useState } from 'react'

import { Box, Divider, Icon, SmallIcon, Colors } from '@factorialco/deprecated-design-system'
import { F0Text } from '@factorialco/f0-react'

import i18n from 'modules/core/lib/i18n'

import DeleteShiftModal from 'modules/shift_management/components/ShiftManagement/DeleteShiftModals/DeleteShiftModal'
import EditShiftSidepanel from 'modules/shift_management/components/ShiftManagement/EditShiftSidepanel'
import SeeShiftSidepanel from 'modules/shift_management/components/ShiftManagement/SeeShiftSidepanel'
import type { Shift } from 'modules/shift_management/lib/types'

interface Props {
  shift: Shift
  readOnly: boolean
  isBackup: boolean
  selected: boolean | undefined
  onCopyToClipboard?: () => void
}

type PanelState = 'none' | 'see' | 'edit' | 'delete'

interface MenuItemProps {
  icon: SmallIcon
  label: string
  onClick: () => void
  color?: Colors
}

const MenuItem = ({ icon, label, onClick, color = 'grey1000' }: MenuItemProps) => (
  <Box
    role="button"
    tabIndex={0}
    flexDirection="row"
    alignItems="center"
    gap="s8"
    padding="s8"
    onClick={onClick}
  >
    <Icon.Small icon={icon} color={color} />
    <span className={color === 'radicalRed800' ? 'text-f1-foreground-critical' : undefined}>
      <F0Text variant="label" content={label} />
    </span>
  </Box>
)

const ShiftCardActionsMenu = ({
  shift,
  readOnly,
  isBackup,
  selected,
  onCopyToClipboard,
}: Props) => {
  const [panel, setPanel] = useState<PanelState>('none')

  const openSee = () => setPanel('see')
  const openEdit = () => setPanel('edit')
  const openDelete = () => setPanel('delete')
  const closePanel = () => setPanel('none')

  const onCopyShiftAction = () => onCopyToClipboard?.()

  const stopSelectionPropagation = (event: MouseEvent) => {
    event.stopPropagation()
  }
  return (
    <>
      {selected && (
        <Box
          role="menu"
          background="grey000White"
          borderRadius={{ all: 'abs008' }}
          border={{ all: { width: 's1', style: 'solid', color: 'grey300' } }}
          style={{
            position: 'absolute',
            top: '100%',
            right: '0',
            left: '0',
            marginTop: '4px',
            zIndex: '10',
            boxShadow: '0 8px 24px rgba(17, 24, 39, 0.12)',
          }}
          onClick={stopSelectionPropagation}
        >
          <MenuItem
            icon="eye-visible"
            label={i18n.t('shift_management.shift_menu_options.show_details')}
            onClick={openSee}
          />

          {!readOnly && !isBackup && (
            <>
              <MenuItem
                icon="pencil"
                label={i18n.t('shift_management.shift_menu_options.edit')}
                onClick={openEdit}
              />
              {onCopyToClipboard && (
                <MenuItem
                  icon="replace"
                  label={i18n.t('common.copy')}
                  onClick={onCopyShiftAction}
                />
              )}
              <Divider />
            </>
          )}

          {!readOnly && (
            <MenuItem
              icon="delete"
              label={i18n.t('shift_management.shift_menu_options.delete')}
              onClick={openDelete}
              color="radicalRed800"
            />
          )}
        </Box>
      )}

      {panel === 'see' && (
        <Box onClick={stopSelectionPropagation}>
          <SeeShiftSidepanel
            shift={shift}
            readOnly={readOnly}
            onClose={closePanel}
            onOpenEditSidepanel={openEdit}
          />
        </Box>
      )}

      {panel === 'edit' && (
        <Box onClick={stopSelectionPropagation}>
          <EditShiftSidepanel shifts={[shift]} onClose={closePanel} />
        </Box>
      )}

      {panel === 'delete' && (
        <DeleteShiftModal shift={shift} isBackup={isBackup} onClose={closePanel} />
      )}
    </>
  )
}

export { ShiftCardActionsMenu }
